<template>
  <section class="booking-pitch-details" :class="{ desktop: $isDesktop }">
    <translate tag="h3" class="section-heading">Pitch details</translate>

    <div class="pt-row name">
      <pu-category-icons :categories="categories" />
      <span class="pt-details">
        {{ booking.pitchtypeNameWithUnitNumber || pitchType.name }}
        <translate v-if="doorCode" :tt="{ doorCode }" class="doorcode">
          Key/door code: {doorCode}
        </translate>
      </span>
    </div>

    <span class="pt-row groundtype" v-if="pitchType.groundType">
      <img
        loading="lazy"
        src="@/assets/images/grass.svg"
        width="19"
        height="14"
        alt=""
      />
      <translate
        tag="p"
        :tt="{
          pitchTypeGroundType: pitchType.groundType,
        }"
      >
        {pitchTypeGroundType} ground type
      </translate>
    </span>

    <span class="pt-row dimensions" v-if="dimensionsDetails">
      <img
        loading="lazy"
        src="@/assets/images/area.svg"
        width="16"
        height="16"
        alt=""
      />
      <div>
        <pitch-type-dimensions :pitch-type="pitchType" />

        <translate
          v-if="booking.typeAndApproxSizeOfUnit"
          tag="p"
          :tt="{
            typeAndApproxSizeOfUnit: booking.typeAndApproxSizeOfUnit,
          }"
        >
          Type and approx size of unit: {typeAndApproxSizeOfUnit}
        </translate>

        <translate
          v-if="
            booking.dimensionsDepth &&
            booking.dimensionsWidth &&
            booking.unitType
          "
          tag="p"
          :tt="{
            dimensionsDepth: Number(booking.dimensionsDepth),
            dimensionsWidth: Number(booking.dimensionsWidth),
            unitType: booking.unitType?.name,
          }"
        >
          Type and approx size of unit: {unitType} {dimensionsWidth} metres x
          {dimensionsDepth} metres
        </translate>
      </div>
    </span>

    <span
      v-if="pitchType.isByo && pitchType.twinAxleAccepted != null"
      class="pt-row"
    >
      <img
        v-if="!pitchType.twinAxleAccepted"
        loading="lazy"
        src="@/assets/images/not-allowed.svg"
        width="16"
        height="16"
        alt=""
      /><span v-else />
      <translate v-if="pitchType.twinAxleAccepted" tag="p">
        Twin-axle vehicles accepted
      </translate>
      <translate v-else tag="p"> Twin-axle vehicles not accepted </translate>
    </span>

    <span
      v-if="showPrerenderedSection(pitchType.bedroomsText)"
      class="pt-row rooms"
    >
      <img
        loading="lazy"
        src="@/assets/images/rooms.svg"
        width="16"
        height="17"
        alt=""
      />
      <span>
        <translate tag="p"> Rooms: </translate>
        <div v-thtml="pitchType.bedroomsText" class="prerendered" />
      </span>
    </span>

    <span v-if="bringBedLinenHtml" class="pt-row linen">
      <img
        loading="lazy"
        src="@/assets/images/exclamation-mark-blue.svg"
        width="17"
        height="17"
        alt=""
      />
      <div
        v-thtml="bringBedLinenHtml"
        class="prerendered preserve-linebreaks"
      />
    </span>

    <template v-if="restrictions">
      <span
        v-for="(restriction, index) in restrictions"
        :key="index"
        class="pt-row restriction"
      >
        <img
          loading="lazy"
          src="@/assets/images/exclamation-mark-blue.svg"
          width="17"
          height="17"
          alt=""
        />
        <p>{{ restriction.name }}</p>
      </span>
    </template>

    <p v-if="pitchType.description">
      {{ pitchType.description }}
    </p>
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { combineCategories } from '~/utility/pitchtype'

interface PitchTypeCategory {
  name: string
  slug: string
}

interface Booking {
  adults: number
  address: string | null
  arrivalFrom: string | null
  arrivalTime: string
  arrivalTo: string | null
  arrive: string
  campsiteUsePermittedArrivalDepartTimes: boolean
  campsite: Campsite
  carRegistrationNumber: string
  changes: {
    arrive?: {
      originalDate: string
      changedDate: string
    }
    depart?: {
      originalDate: string
      changedDate: string
    }
  } | null
  childAges: number[]
  city: string | null
  county: string | null
  departFrom: string | null
  depart: string
  dimensionsDepth: string | null
  dimensionsWidth: string | null
  estimatedTimeOfArrival: string
  groupName: string
  namesOfAllPartyMembers: string
  postcode: string | null
  specialRequests: string
  pitchtypeNameWithUnitNumber?: string
  typeAndApproxSizeOfUnit: string
  unitType: { name: string } | null
}

interface Campsite {
  timezone: string
}

interface PitchType {
  allowedSubcategories: string[]
  bedroomsText: string
  campsite: Campsite
  categories: PitchTypeCategory[]
  description: string
  dimensions: string
  dimensionsDetailed?: {
    areaFt: number | null
    areaM: number | null
    customDimensions: string | null
    depthFt: number | null
    depthM: number | null
    dimensionsType: string | null
    dimensionsTypeCode: string | null
    widthFt: number | null
    widthM: number | null
  }
  groundType: string
  isByo: boolean
  name: string
  negativeCategories: PitchTypeCategory[]
  oneUnitPerPitch: boolean
  requireCarRegistration: boolean
  requirePartyNames: boolean
  twinAxleAccepted: boolean
}

@Component
class BookingPitchDetails extends Vue {
  @Prop({ required: true })
  pitchType: PitchType

  @Prop({ required: true })
  booking: Booking

  @Prop()
  bringBedLinenHtml: string

  @Prop({ required: false })
  doorCode: string

  @Prop()
  restrictions: Array<{
    amenityIcon: string
    name: string
  }>

  showPrerenderedSection(text: string) {
    return text.replace(/\s+/g, ' ').trim().length > 0
  }

  get localeIsEn() {
    return this.$i18n.locale.startsWith('en-')
  }

  get dimensionsDetails() {
    return (
      (this.pitchType.dimensionsDetailed &&
        this.pitchType.dimensionsDetailed.dimensionsType) ||
      (this.pitchType.dimensionsDetailed &&
        this.pitchType.dimensionsDetailed.widthM &&
        this.pitchType.dimensionsDetailed.depthM) ||
      this.booking.typeAndApproxSizeOfUnit ||
      (this.booking.dimensionsDepth &&
        this.booking.dimensionsWidth &&
        this.booking.unitType)
    )
  }

  get categories() {
    try {
      return combineCategories(
        this.pitchType.categories,
        this.pitchType.negativeCategories,
      )
    } catch (e) {
      console.error(e)
      return []
    }
  }
}

export default toNative(BookingPitchDetails)
</script>

<style scoped>
.booking-pitch-details {
  padding: 1em;
  border-bottom: 1px solid var(--colour-page-background);
  border-top: 1px solid var(--colour-page-background);
}

.prerendered,
.booking-pitch-details p,
.pt-row.dimensions :deep(p) {
  font-size: var(--body);
}

.pt-row {
  display: grid;
  grid-template-columns: 19px 1fr;
  place-items: start start;
  gap: 0.35em;
  margin-bottom: 0.5em;
}

.pt-row img,
.pt-row :deep(img) {
  place-self: start center;
  max-width: none;
}

.pt-row.name {
  grid-template-columns: minmax(19px, auto) 1fr;
  margin-bottom: 0.7em;
  font-size: var(--body);
}

.pt-details {
  display: flex;
  flex-direction: column;
  align-items: start;
}

.doorcode {
  background-color: #e1e5ea80;
  border-radius: var(--border-radius);
  padding: 0.25em;
  display: inline-flex;
  align-items: center;
  margin-top: 0.2em;
}

.rooms :deep(table) {
  width: auto;
}

.rooms :deep(table td:first-of-type) {
  padding: 0;
}

.rooms :deep(table td + td) {
  padding: 0.5em 0 0 0;
}

.rooms :deep(table tr) {
  border: none;
}

.booking-pitch-details.desktop .rooms :deep(table) {
  font-size: inherit;
}
</style>
